import React, { useEffect } from 'react';
import { Form, Input, Select, Button, Row, Col } from 'antd';
// *** ADD ICON IMPORTS needed for statusOptions ***
import { MailOutlined, UserOutlined, PhoneOutlined, MessageOutlined, ContainerOutlined, CheckCircleOutlined, CloseCircleOutlined, ClockCircleOutlined } from '@ant-design/icons';

const { TextArea } = Input;
const { Option } = Select;

// *** DEFINE projectTypes HERE ***
const projectTypes = [
    { value: "landingPage", label: "Landing Page" },
    { value: "webApp", label: "Web App" },
    { value: "mobileApp", label: "Mobile App" },
    { value: "eCommerce", label: "E-Commerce Platform" },
    { value: "aiApp", label: "AI-Powered Application" },
    { value: "saas", label: "SaaS Platform" },
    { value: "pwa", label: "Progressive Web App (PWA)" },
    { value: "crmErp", label: "Custom CRM/ERP System" },
    { value: "cms", label: "Content Management System (CMS)" },
    { value: "marketplace", label: "Marketplace Platform" },
    { value: "gamingApp", label: "Gaming App" },
    { value: "iotApp", label: "IoT Application" },
    { value: "blockchainApp", label: "Blockchain Application" },
    { value: "dataDashboard", label: "Data Analytics Dashboard" },
    { value: "customApi", label: "Custom API Development" },
    { value: "other", label: "Other" },
];

// *** DEFINE statusOptions HERE ***
const statusOptions = [
    { value: 'Pending', label: 'Pending', color: 'orange', icon: <ClockCircleOutlined /> },
    { value: 'Confirmed', label: 'Confirmed', color: 'green', icon: <CheckCircleOutlined /> },
    { value: 'Processing', label: 'Processing', color: 'blue', icon: <ClockCircleOutlined /> },
    { value: 'Completed', label: 'Completed', color: 'purple', icon: <CheckCircleOutlined /> },
    { value: 'Rejected', label: 'Rejected', color: 'red', icon: <CloseCircleOutlined /> },
];


const QuoteForm = ({ initialValues, onSubmit, onCancel, loading }) => {
  const [form] = Form.useForm();
  const isEditing = !!initialValues;

  useEffect(() => {
    // Set form fields when initialValues change (for editing) or for new form
    form.setFieldsValue({
        name: '',
        email: '',
        phone: '',
        projectType: undefined, // Use undefined for placeholder to show correctly
        message: '',
        status: 'Pending', // Default status for new quotes
        ...initialValues, // Spread initial values, potentially overriding defaults if editing
    });
  }, [initialValues, form]);

  const handleFinish = (values) => {
    onSubmit(values); // Pass validated values to parent handler
  };

  return (
    <Form
      form={form}
      layout="vertical"
      onFinish={handleFinish}
      // initialValues prop on Formik/Form is sometimes tricky with dynamic defaults,
      // using form.setFieldsValue in useEffect is more reliable here.
    >
      <Row gutter={16}>
        <Col xs={24} sm={12}>
          <Form.Item
            name="name"
            label="Client Name"
            rules={[{ required: true, message: 'Please enter the client name' }]}
          >
            <Input prefix={<UserOutlined />} placeholder="Client Name" />
          </Form.Item>
        </Col>
        <Col xs={24} sm={12}>
          <Form.Item
            name="email"
            label="Client Email"
            rules={[
              { required: true, message: 'Please enter the client email' },
              { type: 'email', message: 'Please enter a valid email' },
            ]}
          >
            <Input prefix={<MailOutlined />} placeholder="Client Email" type="email" />
          </Form.Item>
        </Col>
      </Row>

      <Row gutter={16}>
        <Col xs={24} sm={12}>
          <Form.Item
            name="phone"
            label="Client Phone"
            rules={[
              { required: true, message: 'Please enter the phone number' },
              // { pattern: /^\d{10}$/, message: 'Must be 10 digits' } // Optional stricter validation
            ]}
          >
            <Input prefix={<PhoneOutlined />} placeholder="Phone Number" type="tel"/>
          </Form.Item>
        </Col>
         <Col xs={24} sm={12}>
            <Form.Item
                name="projectType"
                label="Project Type"
                rules={[{ required: true, message: 'Please select a project type' }]}
            >
                {/* *** Use projectTypes defined above *** */}
                <Select prefix={<ContainerOutlined />} placeholder="Select Project Type" allowClear>
                 {projectTypes.map(type => (
                    <Option key={type.value} value={type.value}>
                        {type.label}
                    </Option>
                ))}
                </Select>
            </Form.Item>
        </Col>
      </Row>

       <Form.Item
            name="status"
            label="Quote Status"
            rules={[{ required: true, message: 'Please select a status' }]}
        >
             {/* *** Use statusOptions defined above *** */}
            <Select placeholder="Select Status">
                {statusOptions.map(status => (
                    <Option key={status.value} value={status.value}>
                         {/* Render icon directly if it's already a React element */}
                        {status.icon} <span style={{ marginLeft: 8 }}>{status.label}</span>
                    </Option>
                ))}
            </Select>
       </Form.Item>

      <Form.Item
        name="message"
        label="Message / Requirements"
        rules={[
            { required: true, message: 'Please enter the message or requirements' },
            { min: 10, message: 'Message must be at least 10 characters'}
        ]}
      >
        <TextArea rows={4} placeholder="Details about the quote request" />
      </Form.Item>

      <Form.Item style={{ textAlign: 'right', marginTop: '24px' }}>
        <Button onClick={onCancel} style={{ marginRight: 8 }} disabled={loading}>
          Cancel
        </Button>
        <Button type="primary" htmlType="submit" loading={loading}>
          {isEditing ? 'Save Changes' : 'Add Quote'}
        </Button>
      </Form.Item>
    </Form>
  );
};

export default QuoteForm;